// /admin/v1/content/packages/:id/plates — 底版 (plate) 人脸元数据「人工标框」。
//
// 创想熊式套系 = 干净底版图 + 人工标注的人脸框 (faces)。运营在 admin 里用鼠标
// 在底版图上拖拽画框，给每个框编号 (order) 与可选 role/gender，存为 faces 元数据。
// bbox 用归一化坐标 (0..1)，与底版图尺寸无关，前端 overlay 在任意尺寸都能还原。

import { get, post, put, del, list, type ListResult } from './client';

/** 单个人脸框 — 归一化坐标 (相对底版图宽高，0..1)。 */
export interface PlateFaceBBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PlateFace {
  /** 换脸/上传顺序，从 1 起，唯一。 */
  order: number;
  bbox: PlateFaceBBox;
  /** 可选角色描述 (如 "新郎"/"新娘"/"左"/"右")。 */
  role?: string;
  /** 可选性别提示 (如 "male"/"female")，留空表示不限。 */
  gender?: string;
}

export type PlateStatus = 'candidate' | 'draft' | 'active' | 'archived';

export interface Plate {
  plate_id: number;
  plate_image_url: string;
  /** 底版 OSS key (非签名 URL) — 「设为封面」写入 cover_url 用, 签名 URL 会过期。 */
  plate_image_key?: string;
  faces: PlateFace[];
  status: PlateStatus;
  sort_order: number;
  /** 所属生成批次 (plate-generation-runs); 存量/手工登记底版为空。 */
  run_id?: number;
  /** 实际出图 provider (含降级标注如 jimeng(degraded_from_onething)); 非自产为空。 */
  provider?: string;
}

interface GetPlatesResponse {
  plates: Plate[];
}

/** 拉某套系下的全部底版 (后端返回已签名的可读 plate_image_url)。 */
export async function getPackagePlates(packageId: number): Promise<Plate[]> {
  const resp = await get<GetPlatesResponse>(
    `/admin/v1/content/packages/${packageId}/plates`,
  );
  return resp?.plates ?? [];
}

/** 保存某底版的 faces 元数据 (可选同时流转 status)。 */
export function savePlateFaces(
  packageId: number,
  plateId: number,
  faces: PlateFace[],
  status?: PlateStatus,
): Promise<Plate> {
  const body: { faces: PlateFace[]; status?: PlateStatus } = { faces };
  if (status) body.status = status;
  return put<Plate>(
    `/admin/v1/content/packages/${packageId}/plates/${plateId}`,
    body,
  );
}

export interface CreatePlateInput {
  /** OSS object key (非签名 URL) of the clean plate image. */
  plate_image_key: string;
  faces?: PlateFace[];
  sort_order?: number;
}

/** 新增一张底版。 */
export function createPlate(
  packageId: number,
  input: CreatePlateInput,
): Promise<Plate> {
  return post<Plate>(`/admin/v1/content/packages/${packageId}/plates`, input);
}

/**
 * 从套系现有底版 (package_samples，创想熊导入的 samplePics) 导入为 plate，供标框。
 * 幂等：已存在同图的 plate 跳过。返回最新 plate 列表。
 */
export async function importPlatesFromSamples(packageId: number): Promise<Plate[]> {
  const resp = await post<GetPlatesResponse>(
    `/admin/v1/content/packages/${packageId}/plates/import-from-samples`,
    {},
  );
  return resp?.plates ?? [];
}

/** 「后台生成底版」引擎选择: 即梦 (国风底版质量优先, 默认) 或 onething 默认链。 */
export type PlateAutogenEngine = 'jimeng' | 'onething';

export interface PlateAutogenInput {
  /**
   * txt2img 底版画面 prompt (运营填写)。
   * 套系工作流 (suite-generation-workflow) 用 subject_prompt + scene_variants 展开,
   * 但后端 autogen 仍要求非空 prompt, 故套系页会把 subject_prompt 兜底填进 prompt。
   */
  prompt?: string;
  /** 生成张数 (默认 4); 套系工作流由 场景数×per_scene_count 决定, 可留空。 */
  count?: number;
  /** 生成引擎 (默认 jimeng); 套系工作流走 onething (LoRA + 参考脸换脸)。 */
  engine?: PlateAutogenEngine;
  /** 可选负面词 (即梦会内联进 prompt); 留空用后端默认干净底版负面词。 */
  negative_prompt?: string;

  // --- 套系 LoRA (仅 engine=onething 生效; 即梦忽略) -------------------------
  /** 已训好的套系 LoRA 名 (如 gfcxx_v1)。 */
  lora_name?: string;
  /** LoRA 权重 (默认 0.7)。 */
  lora_weight?: number;
  /** LoRA 触发词 (如 "gfcxx style"); 在中译英之后注入。 */
  lora_trigger?: string;

  // --- suite-generation-workflow 新增 --------------------------------------
  /**
   * 参考脸 OSS 签名读 URL。非空 → 本次每张底版都 ForceFaceSwap 换成这张脸
   * (生产 PuLID/ReActor), 得到统一模特脸样片; 空 → 生成 clean 无脸底版 (现状)。
   */
  reference_face_url?: string;
  /** 主体描述 (中文, 如"一位现代中国女性穿华丽戏曲戏服精致珠花头饰")。 */
  subject_prompt?: string;
  /** 场景变体列表 (中文短语, 如 ["在大本钟前","在伦敦塔桥前"])。 */
  scene_variants?: string[];
  /** 每个场景出几张 (默认 2); 总张数 = scene_variants.length × per_scene_count。 */
  per_scene_count?: number;
}

export interface PlateAutogenResult {
  task_id: string;
  /** 本次生成的批次 id (plate-generation-runs); 候选归属它。 */
  run_id: number;
  engine: string;
  count: number;
  status: string;
}

/**
 * 触发「后台生成底版」: 起 WF_PLATE_AUTOGEN 异步生成 N 张干净底版落 candidate。
 * 生成是异步的, 端点立即返回 task_id + run_id; 候选落到「生成记录」区对应批次,
 * 运营从批次候选「选入套系」后才进标框工作区。
 */
export function triggerPlateAutogen(
  packageId: number,
  input: PlateAutogenInput,
): Promise<PlateAutogenResult> {
  return post<PlateAutogenResult>(
    `/admin/v1/content/packages/${packageId}/plates/autogen`,
    input,
  );
}

// --- plate-generation-runs: 生成批次留档 + 候选挑选 -------------------------

export type PlateRunStatus = 'running' | 'done' | 'failed';

/** 一次「生成底版」批次的留档 (prompt/引擎/张数/实际出图数/状态/时间)。 */
export interface PlateRun {
  run_id: number;
  prompt: string;
  negative_prompt: string;
  /** 请求引擎 (jimeng/onething/'')。 */
  engine: string;
  /** 请求张数。 */
  count: number;
  /** 实际出图并落候选的张数 (收尾回填)。 */
  result_count: number;
  status: PlateRunStatus;
  error: string;
  created_at: string;
}

interface GetPlateRunsResponse {
  runs: PlateRun[];
}

interface GetCandidatesResponse {
  candidates: Plate[];
}

/** 拉某套系历次生成批次 (时间倒序)。 */
export async function listPlateRuns(packageId: number): Promise<PlateRun[]> {
  const resp = await get<GetPlateRunsResponse>(
    `/admin/v1/content/packages/${packageId}/plate-runs`,
  );
  return resp?.runs ?? [];
}

/** 拉某批次的候选底版 (status=candidate & run_id; 含 provider + 现签图 URL)。 */
export async function listRunCandidates(
  packageId: number,
  runId: number,
): Promise<Plate[]> {
  const resp = await get<GetCandidatesResponse>(
    `/admin/v1/content/packages/${packageId}/plate-runs/${runId}/candidates`,
  );
  return resp?.candidates ?? [];
}

/** 「选入套系」: candidate→draft (幂等)。转换后该底版进入标框工作区。 */
export function selectCandidate(
  packageId: number,
  plateId: number,
): Promise<Plate> {
  return post<Plate>(
    `/admin/v1/content/packages/${packageId}/plates/${plateId}/select`,
    {},
  );
}

/** PUT /admin/v1/content/plates/:id/reassign — 候选转移到其他套系 (同 App). */
export function reassignPlate(plateId: number, targetPackageId: number): Promise<Plate> {
  return put<Plate>(`/admin/v1/content/plates/${plateId}/reassign`, { target_package_id: targetPackageId });
}

/** DELETE /admin/v1/content/packages/:pkgId/plate-runs/:runId — 删除失败批次. */
export function deleteFailedRun(packageId: number, runId: number): Promise<void> {
  return del(`/admin/v1/content/packages/${packageId}/plate-runs/${runId}`);
}

export interface LibraryPlate extends Plate {
  package_id: number;
  package_name: string;
}

/** GET /admin/v1/content/plates — 跨套系照片集合 (挑选/转移). */
export function listPlatesLibrary(params: {
  app_id: number;
  status?: string; // 逗号分隔, 默认 candidate
  page?: number;
  page_size?: number;
}): Promise<ListResult<LibraryPlate>> {
  return list<LibraryPlate>('/admin/v1/content/plates', params as unknown as Record<string, unknown>);
}

